import type { CharacterKey } from '@secretario/shared';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ConsentCheck, DocLink } from '../legal/Consent.js';
import { characterImage } from '../../lib/characters.js';
import { useUpdateProfile } from '../../lib/useProfile.js';
import './ai-consent.css';

/** Versão do texto aceito: muda quando o que é enviado ou para quem muda. */
export const AI_CONSENT_VERSION = 2;

/** Evento que abre a folha de qualquer lugar (por exemplo, quando o servidor pede a permissão). */
export const AI_CONSENT_EVENT = 'secretario:permissao-ia';

/** Vale a partir da versão 2, com o envio para fora do Brasil aceito à parte. */
export function hasAiConsent(preferences: Record<string, unknown> | undefined): boolean {
  const value = preferences?.aiConsent as
    { at?: unknown; version?: unknown; transfer?: unknown } | null | undefined;
  return (
    !!value &&
    typeof value === 'object' &&
    typeof value.at === 'string' &&
    typeof value.version === 'number' &&
    value.version >= AI_CONSENT_VERSION &&
    value.transfer === true
  );
}

/** O registro que vale como aceite dos Termos e do uso de inteligência artificial. */
export function aiConsentRecord() {
  return { at: new Date().toISOString(), version: AI_CONSENT_VERSION, transfer: true };
}

/**
 * Quem entrou antes do aceite único (Termos + Privacidade cobrem a inteligência
 * artificial) vê esta folha uma vez: uma frase, a caixa "li e concordo" com os
 * documentos ao toque, e segue. Quem se cadastra hoje já aceita na entrada.
 */
export function AiConsentSheet({
  characterKey,
  secretaryName,
  onClose,
}: {
  characterKey: CharacterKey;
  secretaryName: string;
  onClose: () => void;
}) {
  const update = useUpdateProfile();
  const [failed, setFailed] = useState(false);
  const [agreed, setAgreed] = useState(false);

  async function allow() {
    if (!agreed) return;
    setFailed(false);
    try {
      await update.mutateAsync({ preferences: { aiConsent: aiConsentRecord() } });
      onClose();
    } catch {
      setFailed(true);
    }
  }

  return createPortal(
    <div className="aic-back" onClick={onClose}>
      <div
        className="aic"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aic-title"
        onClick={(event) => event.stopPropagation()}
      >
        <img className="aic-art" src={characterImage(characterKey, 'simpatico')} alt="" />
        <div className="aic-body">
          <div className="aic-kicker">Antes de começar</div>
          <h2 id="aic-title">Um aceite só, e a gente segue</h2>
          <p className="aic-lead">
            Pra te responder, eu, {secretaryName}, uso inteligência artificial: seus pedidos passam
            por esse serviço só pra montar a resposta. Nada treina modelos, e nada é vendido.
          </p>
          <ConsentCheck checked={agreed} onChange={setAgreed}>
            Li e concordo com os <DocLink doc="termos">Termos de uso</DocLink> e a{' '}
            <DocLink doc="privacidade">Política de Privacidade</DocLink>.
          </ConsentCheck>
          {failed && <p className="aic-error">Não consegui guardar agora. Tenta de novo?</p>}
          <div className="aic-acts">
            <button
              className="aic-yes"
              onClick={() => void allow()}
              disabled={update.isPending || !agreed}
            >
              {update.isPending ? 'Guardando…' : 'Continuar'}
            </button>
            <button className="aic-no" onClick={onClose} disabled={update.isPending}>
              Agora não
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
